<!--
 * @Description: 访客计划
 * @Author: mdb
 * @Date: 2022-07-18 19:37:48
 * @LastEditTime: 2023-03-23 16:18:24
 * @LastEditors: cxh
-->
<template>
  <dc-layout-main :is-breadcrumb="true">
    <dc-layout-content>
      <cos-table
        serial
        :columns="columns"
        v-model:search="queryForm"
        @fetch="getList"
        @mask="showMask"
        ref="tableRef"
        tableId="DcCosLbuVisitorProgram"
      >
        <template #search>
          <a-form :model="queryForm">
            <dc-form-item label="客户名称" :span="12">
              <a-textarea
                v-model:value="queryForm.customerName"
                :autoSize="{ minRows: 1, maxRows: 4 }"
                placeholder="请输入"
              />
            </dc-form-item>
            <dc-form-item label="客户代码" :span="12">
              <a-textarea
                v-model:value="queryForm.tmsCode"
                :autoSize="{ minRows: 1, maxRows: 4 }"
                placeholder="请输入"
              />
            </dc-form-item>
            <dc-form-item label="计划状态">
              <a-radio-group
                v-model:value="queryForm.status"
                optionType="button"
                :options="planStatusEnumAll"
                @change="handleQueryChange('search')"
              />
            </dc-form-item>
            <dc-form-item label="客户负责人">
              <UserSelect permission v-model:value="queryForm.dutyByList" placeholder="请选择" mode="multiple" allowClear />
            </dc-form-item>
            <dc-form-item label="联系人姓名">
              <a-input v-model:value.trim="queryForm.contactName" :maxlength="100" placeholder="请输入" allowClear />
            </dc-form-item>
            <dc-form-item label="手机号">
              <a-input v-model:value.trim="queryForm.contactMobile" :maxlength="100" placeholder="请输入" allowClear />
            </dc-form-item>
            <dc-form-item label="客户区域">
              <CustomerAreaCascader type="search" appId="cos_lbu" v-model:ids="queryForm.areaIdList" />
            </dc-form-item>
            <dc-form-item label="员工区域">
              <CustomerAreaCascader type="search" appId="cos_lbu" v-model:ids="queryForm.employeeRegionList" />
            </dc-form-item>
            <dc-form-item label="时间查询">
              <a-input-group compact>
                <a-select v-model:value="queryForm.timeType" :options="timeTypeEnum" style="width: 33%" />
                <CosRangePicker
                  v-model:startTime="queryForm.startTime"
                  v-model:endTime="queryForm.endTime"
                  style="width: 67%"
                />
              </a-input-group>
            </dc-form-item>
            <dc-form-item label="客户ID">
              <a-input v-model:value="queryForm.cosId" :maxlength="100" placeholder="请输入" allowClear />
            </dc-form-item>
            <dc-form-item label="拜访方式">
              <ParamCodesSelect
                v-model:value="queryForm.mode"
                :options="paramCodeData.lbu_follow_mode"
                allowClear
              />
            </dc-form-item>
          </a-form>
        </template>

        <template #button>
          <a-button v-permission="'lbu_visitor_add_btn'" type="primary" @click="triggerAddOrEdit()">新增</a-button>
          <ExportButton
            v-permission="'lbu_visitor_export_btn'"
            appId="cos_lbu"
            taskType="307"
            :search="customerListFormat(queryForm)"
            >导出</ExportButton
          >
        </template>

        <template #bodyCell="{ column, record, text }">
          <template v-if="column.dataIndex === 'status'">
            <dc-status :enums="statusEnum" :value="text" />
          </template>
          <!-- 当状态为《已取消》和《已拜访》（status=3、4）时不可进行任何操作 -->
          <template v-if="column.dataIndex === 'action'">
            <SellerButton
              v-permission="'lbu_visitor_complete_btn'"
              type="link"
              :workCode="record.dutyBy"
              :disabled="[3, 4].includes(record.status)"
              @click="handleVisitComplete(record)"
              >完成计划</SellerButton
            >
            <SellerButton
              v-permission="'lbu_visitor_cancel_btn'"
              type="link"
              :workCode="record.dutyBy"
              :disabled="[3, 4].includes(record.status)"
              @click="handleCancel(record)"
              >取消</SellerButton
            >
            <a-button
              v-permission="'lbu_visitor_del_btn'"
              type="link"
              :disabled="[3, 4].includes(record.status)"
              @click="handleDelete(record)"
              >删除</a-button
            >
          </template>
        </template>
      </cos-table>
    </dc-layout-content>
  </dc-layout-main>
</template>

<script setup lang="ts">
import { reactive, computed, ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { Modal as AModal } from 'ant-design-vue'
import Modal from '@/hooks/modalType'
import { message } from '@dc-platform/dc-components'
import CustomerAreaCascader from '@/components/CustomerAreaCascader/index.vue'
import { planStatusEnum, remindTypeEnum, customerStageEnum } from '../dataConfig'
import { listByParamCodes } from '@/api/common'
import {
  visitorProgramList,
  visitorProgramCancel,
  visitorProgramDelete,
  visitorProgramDetail
} from '@/api/yuntuSales/followUpManage'
import { customerListFormat } from '@/utils'
import type { ParamCodeItem } from '@/typings/common'
import type { LbuVisitorProgramParams, LbuVisitorProgramListItem } from '@/typings/api/followUpManage'
const router = useRouter()

// 计划状态枚举
const planStatusEnumAll = [{ label: '全部', value: undefined }, ...planStatusEnum]
const statusEnum = planStatusEnum.map((item) => ({
  name: item.label,
  value: item.value,
  color: item.color
}))
// 时间类型枚举
const timeTypeEnum = [
  { label: '创建时间', value: 0 },
  { label: '计划拜访时间', value: 1 }
]
const paramCodeData: {
  [key: string]: ParamCodeItem[]
} = reactive({
  lbu_follow_purpose: [], // 拜访目的
  lbu_follow_mode: [] // 拜访方式（跟进方式）
})
// 获取业务参数
const getByParamCodes = () => {
  listByParamCodes({
    appId: 'cos_lbu',
    paramCodes: ['lbu_follow_purpose', 'lbu_follow_mode']
  }).then((res) => {
    Object.assign(paramCodeData, res.data)
  })
}
onMounted(() => {
  getByParamCodes()
})

const tableRef = ref()
const queryForm = reactive<LbuVisitorProgramParams>({ timeType: 0 })

const columns = computed(() => [
  {
    title: '计划标题',
    dataIndex: 'title',
    cosType: 'link',
    path: '/lbu/visitorProgram/view',
    query: ['id'],
    fixed: 'left',
    width: 200
  },
  { title: '客户代码', dataIndex: 'tmsCode' },
  { title: '客户名称', dataIndex: 'customerName', width: 200 },
  { title: '客户ID', dataIndex: 'visitRefId', width: 200 },
  { title: '客户阶段', dataIndex: 'customerStage', dicData: customerStageEnum },
  { title: '拜访方式', dataIndex: 'mode', dicData: paramCodeData.lbu_follow_mode },
  { title: '计划拜访时间', dataIndex: ['startTime', 'endTime'], cosType: 'timeRange', width: 320, sorter: true },
  { title: '实际拜访时间', dataIndex: 'finishTime', width: 190, sorter: true },
  { title: '拜访目的', dataIndex: 'purpose', dicData: paramCodeData.lbu_follow_purpose },
  { title: '联系人姓名', dataIndex: 'contactName', cosType: 'mask' },
  { title: '手机号', dataIndex: 'contactMobile', cosType: 'mask' },
  { title: '地址', dataIndex: 'customerAddress' },
  { title: '状态', dataIndex: 'status' },
  { title: '提醒计划', dataIndex: 'remindType', dicData: remindTypeEnum },
  { title: '提醒时间', dataIndex: 'remindTime' },
  { title: '备注', dataIndex: 'remark' },
  { title: '创建人', dataIndex: 'createByName' },
  { title: '创建时间', dataIndex: 'createTime', width: 190, sorter: true },
  { title: '更新人', dataIndex: 'updateByName' },
  { title: '更新时间', dataIndex: 'updateTime', width: 190, sorter: true },
  { title: '操作', dataIndex: 'action', width: 200, fixed: 'right' }
])
const getList = (queryForm: LbuVisitorProgramParams, callBack: (res: any) => void) => {
  const params = customerListFormat(queryForm) // 处理客户名称和客户编码的批量查询参数
  const cosId = queryForm.cosId
  if (cosId !== undefined && cosId.length > 0) {
    params.cosIdList = cosId.split(' ')
    params.cosId = undefined
  }
  visitorProgramList(params).then((res) => {
    callBack(res)
  })
}
const showMask = (record: LbuVisitorProgramListItem, callBack: (res: any) => void) => {
  visitorProgramDetail(record.id).then((res) => {
    callBack(res.data)
  })
}
const handleQueryChange = (type?: 'search' | 'fetch') => {
  if (type === 'search') {
    tableRef.value?.search()
  } else {
    tableRef.value?.fetch()
  }
}

// 新增编辑函数
const triggerAddOrEdit = (id?: string) => {
  if (id) {
    router.push({ path: '/lbu/visitorProgram/edit', query: { id } })
  } else {
    router.push('/lbu/visitorProgram/add')
  }
}
const handleVisitComplete = (row: LbuVisitorProgramListItem) => {
  router.push({ path: '/lbu/followUpRecord/visitingPlan', query: { planId: row.id } })
}
const handleCancel = (row: LbuVisitorProgramListItem) => {
  AModal.warning({
    closable: true,
    title: '确定取消计划吗？',
    content: `取消【${row.title}】，取消后不可进行其他操作，请谨慎操作。`,
    okCancel: true,
    cancelText: '取消',
    okText: '确定',
    onOk() {
      visitorProgramCancel(row.id).then(() => {
        message.success('取消计划成功')
        tableRef.value?.fetch()
      })
    }
  })
}

const handleDelete = (row: LbuVisitorProgramListItem) => {
  Modal.delete({
    name: row.title,
    onOk() {
      visitorProgramDelete(row.id).then(() => {
        message.success('删除计划成功')
        tableRef.value?.delFetch()
      })
    }
  })
}
</script>
<script lang="ts">
export default { name: 'DcCosLbuVisitorProgram' }
</script>

<style scoped></style>
